import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'
import type { DashboardData, DashboardProps, Status } from './Dashboard.types'
import useQueryGet from 'hooks/useQueryGet'
import { GridBlock } from './features/grid-block'
import { StatusTypes } from 'pages/Applications/applicationStatuses'
import { ThumbUp } from 'assets/icons/thumb-up'
import { MailIcon } from 'assets/icons/mail'
import { MessageIcon } from 'assets/icons/message'
import { ReceiptIcon } from 'assets/icons/receipt'
import { ThumbDownIcon } from 'assets/icons/thumb-down'
import { AlertTriangle } from 'assets/icons/alert-triangle'
import { PrizeWinnerIcon } from 'assets/icons/prize-winner'
import { PrizeLoseIcon } from 'assets/icons/prize-lose'
// import { GameIcon } from 'assets/icons/game-icon'
import { TrashIcon } from 'assets/icons/trash'
import { RangeData } from './features/range-data'
import { Box, CircularProgress, Typography } from '@mui/material'
import { getRangeValues } from './utils/get-range-values'
import { useMemo } from 'react'
import { useAppContext } from 'context/AppContext'
import { subDays } from 'date-fns'

const {
  EmailVerification,
  SmsVerification,
  ReciptValidation,
  Rejected,
  Fraud,
  PrizeDrawWinner,
  PrizeDrawLoser,
  Deleted,
  AcceptedEmbargo,
} = StatusTypes

// const gameMarkets = (process.env.REACT_APP_GAME_MARKETS || '').split(',')

const getCount = (statuses: Status[], status: StatusTypes) => {
  return Number(statuses.find(item => item.status === status)?.count) || 0
}

const getPercent = (prevVal: number, current: number, range: string | null) => {
  if (!range) {
    return
  }
  if (!prevVal && current) {
    return 100
  }
  if (prevVal && !current) {
    return -100
  }
  if (!current) {
    return
  }
  return Math.round((current / current) * 100)
}

const processedData = (statuses: Status[], status: StatusTypes, loading: boolean) => {
  if (loading) {
    return <CircularProgress size={12} />
  }

  return `${getCount(statuses, status)} processed by now`
}

const countData = (statuses: Status[], status: StatusTypes, loading: boolean) => {
  if (loading) {
    return <CircularProgress size={24} />
  }

  return getCount(statuses, status)
}

const Dashboard2 = ({ market, selectedMarketId, range }: DashboardProps): JSX.Element => {
  const { t } = useTranslation()
  const { markets } = useAppContext()

  const rangeData = useMemo(() => {
    return getRangeValues(range)
  }, [range])

  const { data, isLoading } = useQueryGet<DashboardData>({
    queryKey: ['dashboard-applications', market?.id, selectedMarketId, rangeData?.from, rangeData?.to],
    endpoint: 'applications/dashboard',
    options: {
      params: {
        marketId: selectedMarketId === '-1' ? '' : market?.id,
        from: rangeData?.from,
        to: rangeData?.to,
      },
    },
  })

  const { data: prevData } = useQueryGet<DashboardData>({
    queryKey: [
      `dashboard-all-applications-previous-range-${market?.id}-${selectedMarketId}`,
      rangeData ? getRangeValues(range, subDays(rangeData?.from, 1))?.from : undefined,
      rangeData ? getRangeValues(range, subDays(rangeData?.from, 1))?.to : undefined,
    ],
    endpoint: 'applications/dashboard',
    options: {
      params: {
        marketId: selectedMarketId === '-1' ? '' : market?.id,
        from: rangeData ? getRangeValues(range, subDays(rangeData?.from, 1))?.from : undefined,
        to: rangeData ? getRangeValues(range, subDays(rangeData?.from, 1))?.to : undefined,
      },
    },
  })

  const { data: allData, isLoading: allDataLoading } = useQueryGet<DashboardData>({
    queryKey: [`dashboard-all-applications-${market?.id}-${selectedMarketId}`],
    endpoint: 'applications/dashboard',
    options: {
      params: {
        marketId: selectedMarketId === '-1' ? '' : market?.id,
      },
    },
  })

  const formattedData = useMemo((): DashboardData => {
    if (data === null || data === undefined || 'error' in data) {
      // return <RequestStatus data={data} isLoading={isLoading} />
      return {
        total: 0,
        range: [],
        statuses: [],
        unique: 0,
      }
    }

    return { ...data, range: data.range ?? [] }
  }, [data])

  const formattedAllData = useMemo((): DashboardData => {
    if (allData === null || allData === undefined || 'error' in allData) {
      // return <RequestStatus data={data} isLoading={isLoading} />
      return {
        total: 0,
        range: [],
        statuses: [],
        unique: 0,
      }
    }

    return { ...allData, range: allData.range ?? [] }
  }, [allData])

  const formattedPrevData = useMemo((): DashboardData => {
    if (prevData === null || prevData === undefined || 'error' in prevData || range === null) {
      // return <RequestStatus data={data} isLoading={isLoading} />
      return {
        total: 0,
        range: [],
        statuses: [],
        unique: 0,
      }
    }

    return { ...prevData, range: prevData.range ?? [] }
  }, [prevData, range])

  const isAll = selectedMarketId === 'all'
  const isEmailVerification = market?.verification === 'email'
  const isSMSVerification = market?.verification === 'sms'

  return (
    <Box mt={4}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={3}>
          <GridBlock
            preTitle={t('dashboard.applicationsInProgress')}
            color="#E6F4D7"
            title={t('applicationStatus.accepted')}
            value={countData(formattedData.statuses, StatusTypes.Accepted, isLoading)}
            desc={processedData(formattedAllData.statuses, StatusTypes.Accepted, allDataLoading)}
            icon={<ThumbUp />}
          />
        </Grid>

        {(isEmailVerification || isAll || isSMSVerification) && (
          <Grid item xs={12} md={6} lg={3}>
            <GridBlock
              preTitle={<>&nbsp;</>}
              color="#F4EBFF"
              title={t('applicationStatus.emailVerification')}
              value={countData(formattedData.statuses, EmailVerification, isLoading)}
              desc={processedData(formattedAllData.statuses, EmailVerification, allDataLoading)}
              percent={getPercent(
                getCount(formattedPrevData.statuses, EmailVerification),
                getCount(formattedData.statuses, EmailVerification),
                range
              )}
              icon={<MailIcon />}
            />
          </Grid>
        )}

        <Grid item xs={12} md={isEmailVerification || isAll || isSMSVerification ? 6 : 9}>
          <GridBlock
            disablePaddingBottom
            preTitle={t('dashboard.totalApplications')}
            preTitleBorder
            value={
              <>
                <div>{isLoading ? <CircularProgress /> : formattedData.total}</div>
                <RangeData range={range} data={isLoading ? [] : formattedData.range} />
              </>
            }
            desc=""
          />
        </Grid>

        {(market?.verification === 'sms' || (isAll && markets?.data.some(el => el.verification === 'sms'))) && (
          <Grid item xs={12} md={6} lg={3}>
            <GridBlock
              color="#F4EBFF"
              title={t('applicationStatus.smsVerification')}
              value={countData(formattedData.statuses, SmsVerification, isLoading)}
              desc={processedData(formattedAllData.statuses, SmsVerification, allDataLoading)}
              percent={getPercent(
                getCount(formattedPrevData.statuses, SmsVerification),
                getCount(formattedData.statuses, SmsVerification),
                range
              )}
              icon={<MessageIcon />}
            />
          </Grid>
        )}

        <Grid item xs={12} md={6} lg={3}>
          <GridBlock
            color="#D1E9FF"
            title={t('applicationStatus.receiptValidation')}
            value={countData(formattedData.statuses, ReciptValidation, isLoading)}
            desc={processedData(formattedAllData.statuses, ReciptValidation, allDataLoading)}
            percent={getPercent(
              getCount(formattedPrevData.statuses, ReciptValidation),
              getCount(formattedData.statuses, ReciptValidation),
              range
            )}
            icon={<ReceiptIcon />}
          />
        </Grid>

        {market?.code === 'PL' && (
          <Grid item xs={12} md={6} lg={3}>
            <GridBlock
              color="#E6F4D7"
              title={t('applicationStatus.acceptedEmbargo')}
              value={countData(formattedData.statuses, AcceptedEmbargo, isLoading)}
              desc={processedData(formattedAllData.statuses, AcceptedEmbargo, allDataLoading)}
              percent={getPercent(
                getCount(formattedPrevData.statuses, AcceptedEmbargo),
                getCount(formattedData.statuses, AcceptedEmbargo),
                range
              )}
              icon={<ThumbUp />}
            />
          </Grid>
        )}

        <Grid item xs={12} md={6} lg={3}>
          <GridBlock
            color="#FEE4E2"
            title={t('applicationStatus.rejected')}
            value={countData(formattedData.statuses, Rejected, isLoading)}
            desc={processedData(formattedAllData.statuses, Rejected, allDataLoading)}
            percent={getPercent(
              getCount(formattedPrevData.statuses, Rejected),
              getCount(formattedData.statuses, Rejected),
              range
            )}
            icon={<ThumbDownIcon />}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <GridBlock
            color="#F4EBFF"
            title={t('applicationStatus.fraud')}
            value={countData(formattedData.statuses, Fraud, isLoading)}
            desc={processedData(formattedAllData.statuses, Fraud, allDataLoading)}
            percent={getPercent(
              getCount(formattedPrevData.statuses, Fraud),
              getCount(formattedData.statuses, Fraud),
              range
            )}
            icon={<AlertTriangle />}
          />
        </Grid>

        {(market?.flow === 'pd' || (isAll && markets?.data.some(el => el.flow === 'pd'))) && (
          <Grid item xs={12} md={6} lg={3}>
            <GridBlock
              color="#E6F4D7"
              title={t('applicationStatus.prizeDrawWinner')}
              value={countData(formattedData.statuses, PrizeDrawWinner, isLoading)}
              desc={processedData(formattedAllData.statuses, PrizeDrawWinner, allDataLoading)}
              percent={getPercent(
                getCount(formattedPrevData.statuses, PrizeDrawWinner),
                getCount(formattedData.statuses, PrizeDrawWinner),
                range
              )}
              icon={<PrizeWinnerIcon />}
            />
          </Grid>
        )}

        {(market?.flow === 'pd' || (isAll && markets?.data.some(el => el.flow === 'pd'))) && (
          <Grid item xs={12} md={6} lg={3}>
            <GridBlock
              color="#FEF0C7"
              title={t('applicationStatus.prizeDrawLoser')}
              value={countData(formattedData.statuses, PrizeDrawLoser, isLoading)}
              desc={processedData(formattedAllData.statuses, PrizeDrawLoser, allDataLoading)}
              percent={getPercent(
                getCount(formattedPrevData.statuses, PrizeDrawLoser),
                getCount(formattedData.statuses, PrizeDrawLoser),
                range
              )}
              icon={<PrizeLoseIcon />}
            />
          </Grid>
        )}

        {/* {(gameMarkets.includes(market?.code.toLowerCase() || 'not-found') ||
          (isAll && markets?.data.some(el => gameMarkets.includes(el.code.toLowerCase())))) && (
          <Grid item xs={12} md={6} lg={3}>
            <GridBlock
              color="#D1E9FF"
              title={t('applicationStatus.game')}
              value={countData(formattedData.statuses, Game, isLoading)}
              desc={processedData(formattedAllData.statuses, Game, allDataLoading)}
              percent={getPercent(
                getCount(formattedPrevData.statuses, Game),
                getCount(formattedData.statuses, Game),
                range
              )}
              icon={<GameIcon />}
            />
          </Grid>
        )} */}

        <Grid item xs={12} md={6} lg={3}>
          <GridBlock
            color="#F5F5F5"
            title={t('applicationStatus.deleted')}
            value={countData(formattedData.statuses, Deleted, isLoading)}
            desc={processedData(formattedAllData.statuses, Deleted, allDataLoading)}
            percent={getPercent(
              getCount(formattedPrevData.statuses, Deleted),
              getCount(formattedData.statuses, Deleted),
              range
            )}
            icon={<TrashIcon />}
          />
        </Grid>
      </Grid>

      {range && (
        <Typography mt={2} variant="body2" color="grey.600">
          * vs previous range
        </Typography>
      )}
    </Box>
  )
}

export default Dashboard2
